import React from "react";
import { statusStyle, STATUS_STYLES } from "./tokens";

// =====================================================================
// Club Radar V2 — pills & badges (DESIGN-SPEC §4 items 4/5/7/8/9).
// Purely presentational, exact artboard values. ALL labels arrive as
// props/children — sibling-kit i18n pattern (club-network/v2 cnPills:
// "All strings pass through as props/children so each surface keeps its
// own copy"); the kit bakes in no copy. Status labels stay data-driven
// (swappable Watching/Recommended/Rejected ↔ Tracking/Shortlisted/
// Passed sets) — never a closed enum here.
// =====================================================================

// Base pill: r999, inline-flex. Everything else is a preset over this.
export const Pill = ({ bg, color, pad = "3px 9px", fontSize = 11, fontWeight = 700, gap, style, children, ...rest }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      ...(gap != null ? { gap } : null),
      padding: pad,
      borderRadius: 999,
      background: bg,
      color,
      fontSize,
      fontWeight,
      whiteSpace: "nowrap",
      flex: "none",
      ...style,
    }}
    {...rest}
  >
    {children}
  </span>
);

// ── Status pill (display) — §4 item 5 ────────────────────────────────
// Tint bg + strong fg + leading dot in the fg color. Desktop table:
// pad 3px 9px, 12px/700, dot 6×6; mobile row: pad 3px 8px, 11px/700,
// dot 5×5. Label = the platform's status label (data-driven); styling
// resolves via statusStyle() with a watching fallback for unknown keys.
export const StatusPill = ({ status, label, mobile = false, styles = STATUS_STYLES, style, ...rest }) => {
  const s = statusStyle(status, styles);
  const dot = mobile ? 5 : 6;
  return (
    <Pill
      bg={s.bg}
      color={s.fg}
      pad={mobile ? "3px 8px" : "3px 9px"}
      fontSize={mobile ? 11 : 12}
      gap={5}
      style={style}
      {...rest}
    >
      <span
        data-testid="cr-status-dot"
        style={{ width: dot, height: dot, borderRadius: "50%", background: s.fg, flex: "none" }}
      />
      {label}
    </Pill>
  );
};

// ── Position pill — §4 item 8 ────────────────────────────────────────
// `#E8F1FF`/`#1B6FE0`, 9.5–11.5px/700 per surface (row 10.5, drawer 11,
// profile 11.5, snapshot mini-row 9.5).
export const PositionPill = ({ fontSize = 10.5, pad = "2px 7px", children, ...rest }) => (
  <Pill bg="#E8F1FF" color="#1B6FE0" pad={pad} fontSize={fontSize} {...rest}>
    {children}
  </Pill>
);

// ── Label chip — §4 item 7 ───────────────────────────────────────────
// Colors are DATA-DRIVEN from the club's real label palette (the
// artboard's Priority/U23 pairs are sample data) — pass bg/color from
// the platform's label hex (e.g. radarLabelHex + tint at the call site).
export const LabelChip = ({ bg = "#F3F4F6", color = "#4B5563", pad = "2px 8px", fontSize = 10.5, children, ...rest }) => (
  <Pill bg={bg} color={color} pad={pad} fontSize={fontSize} {...rest}>
    {children}
  </Pill>
);

// Dashed add-pill ("+ Label", "+ Copy to list") — DASH-PILL.
export const DashedAddPill = ({ pad = "3px 9px", fontSize = 11, style, children, ...rest }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      padding: pad,
      borderRadius: 999,
      border: "1.5px dashed #CBD5E1",
      background: "transparent",
      color: "#6B7280",
      fontSize,
      fontWeight: 700,
      whiteSpace: "nowrap",
      flex: "none",
      cursor: rest.onClick ? "pointer" : undefined,
      ...style,
    }}
    {...rest}
  >
    {children}
  </span>
);

// ── Share-tier badge family — §4 item 9 / frame 2c ───────────────────
// tone "neutral" → Personal (`#F3F4F6`/`#4B5563`); tone "shared" →
// Shared…/Shared with club (`#E8F1FF`/`#1B6FE0`); tone "sharedOnTint" →
// white-bg variant on the ACTIVE (blue) sidebar row (1b). The TEXT
// ("Personal", "Shared · 3 people", …) is composed by the caller from
// real state — counts are never baked in.
const SHARE_TONES = {
  neutral: { bg: "#F3F4F6", color: "#4B5563" },
  shared: { bg: "#E8F1FF", color: "#1B6FE0" },
  sharedOnTint: { bg: "#fff", color: "#1B6FE0" },
};
export const ShareBadge = ({ tone = "shared", pad = "3px 10px", fontSize = 11.5, children, ...rest }) => {
  const t = SHARE_TONES[tone] || SHARE_TONES.shared;
  return (
    <Pill bg={t.bg} color={t.color} pad={pad} fontSize={fontSize} {...rest}>
      {children}
    </Pill>
  );
};

// Small sidebar/mobile "Shared" chip variant (pad 1px 7px, 10px/700).
export const SharedChip = ({ onTint = false, children, ...rest }) => (
  <ShareBadge tone={onTint ? "sharedOnTint" : "shared"} pad="1px 7px" fontSize={10} {...rest}>
    {children}
  </ShareBadge>
);

// ── Filter chip — §4 item 4 (1a/1b/1e) ───────────────────────────────
// Active: `#E8F1FF`/`#1B6FE0` 700 + border `#D6E7FF` (desktop 1a);
// inactive: white, border `#E5E7EB`, `#6B7280` 600. Rendered as a real
// <button> for wiring; counts inside children come from live data.
export const FilterChip = ({ active = false, mobile = false, style, children, ...rest }) => (
  <button
    type="button"
    style={{
      display: "inline-flex",
      alignItems: "center",
      padding: mobile ? "5px 10px" : "5px 12px",
      borderRadius: 999,
      border: active ? "1px solid #D6E7FF" : "1px solid #E5E7EB",
      background: active ? "#E8F1FF" : "#fff",
      color: active ? "#1B6FE0" : "#6B7280",
      fontSize: mobile ? 12 : 12.5,
      fontWeight: active ? 700 : 600,
      whiteSpace: "nowrap",
      cursor: "pointer",
      fontFamily: "inherit",
      ...style,
    }}
    {...rest}
  >
    {children}
  </button>
);

// ── Eyebrow — small-caps section label (all frames) ──────────────────
// 10.5–12px/700, letter-spacing .06–.08em, `#9CA3AF`.
export const Eyebrow = ({ fontSize = 10.5, spacing = ".08em", color = "#9CA3AF", style, children, ...rest }) => (
  <div
    style={{
      fontSize,
      fontWeight: 700,
      letterSpacing: spacing,
      color,
      textTransform: "uppercase",
      ...style,
    }}
    {...rest}
  >
    {children}
  </div>
);
